import type { ResultOf } from '@graphql-typed-document-node/core'
import dayjs from 'dayjs'
import { GET_FILTERED_NOVELTIES } from '@/queries'
import {
  CompatibleAccuracyType,
  formatDate,
  getSuffixFromVolumeVersion,
  getVersionFromVolume,
  formatVolumeRange,
  NoveltiesFilterProps,
  ReleaseEntrySorts,
  getFormattedVolumeNumberFromVersionPretty,
  formatVolumeNumber,
  hasNonZeroPrice,
  normalizeSearchInput,
} from '@/utils'
import {
  NoveltyOrderByInput,
  NoveltyRelationTypeType,
  NoveltyWhereInput,
  OrderDirection,
} from '../../../../__generated__/graphql.ts'
import { NOT_IN_COLLECTION_MODE } from 'atsume-query-core'
import { LineBuilderConfig, PreviewEntryData } from '@/components/PreviewEntry.vue'
import { useUserListsStore } from '@/stores/user-lists'

export type { NoveltiesFilterProps }

type NoveltyResultItem = ResultOf<typeof GET_FILTERED_NOVELTIES>['filteredNovelties'][number]

export const noveltiesLineBuilder: LineBuilderConfig = {
  preTitleLine: (item, { t }) => {
    const formattedDate = formatDate(item.releaseDate, item.releaseDateAccuracy)
    return t(formattedDate ? 'publisherAndDateLine' : 'publisherLine', {
      publisher: item.publisherName,
      date: formattedDate,
    })
  },
  titleLine: (item) => item.localizedSeriesTitle,
  postTitleLine: (item, { t, getVolumeSuffixFromId, formatPrice }) => {
    const volumeSuffix = item.version
      ? getVolumeSuffixFromId(getSuffixFromVolumeVersion(item.version)).abbreviation
      : ''

    const showPrice = hasNonZeroPrice(item.price)
    if (item.relationType === 'volume') {
      const prefix = t('volumePrefix')
      const lineKey = showPrice
        ? volumeSuffix
          ? 'releaseEntryPriceLineWithSuffix'
          : 'releaseEntryPriceLine'
        : volumeSuffix
          ? 'releaseEntryLineWithSuffix'
          : 'releaseEntryLine'
      return t(lineKey, {
        prefix,
        number: getFormattedVolumeNumberFromVersionPretty(item.version),
        price: formatPrice(item.price),
        currency: '€',
        volumeSuffix: volumeSuffix,
      })
    } else {
      const spansEntries = item.collectionSpansEntries ?? false
      const volumeCount = item.collectionVolumeCount ?? (item.collectionVolumes ?? []).length
      const collectionVolumeRange = spansEntries
        ? '-'
        : formatVolumeRange(item.collectionVolumes ?? [])
      const isEmptySlipcase = collectionVolumeRange === '-' && !spansEntries
      const prefix =
        item.name === 'bundle'
          ? t('collectionPrefix')
          : isEmptySlipcase
            ? t('emptySlipcase')
            : t('slipCasePrefix')
      const lineKey = showPrice
        ? spansEntries
          ? 'collectionEntryLineVolumeCount'
          : collectionVolumeRange === '-'
            ? 'collectionEntryLineNoVolumes'
            : 'collectionEntryLine'
        : spansEntries
          ? 'collectionEntryLineVolumeCountNoPrice'
          : collectionVolumeRange === '-'
            ? 'collectionEntryLineNoVolumesNoPrice'
            : 'collectionEntryLineNoPrice'
      const formattedNumber = formatVolumeNumber(item.number.toFixed(1))
      return t(lineKey, {
        prefix,
        range: collectionVolumeRange,
        number: formattedNumber ? ` ${formattedNumber}` : '',
        price: formatPrice(item.price),
        currency: '€',
        volumeSuffix: volumeSuffix,
        count: volumeCount,
      })
    }
  },
}

// Horizontal preview rows are single-line, so collections drop the number and
// volume range that the overview shows and keep only the prefix and price.
export const horizontalNoveltiesLineBuilder: LineBuilderConfig = {
  ...noveltiesLineBuilder,
  preTitleLine: () => '',
  postTitleLine: (item, ctx) => {
    if (item.relationType === 'volume') {
      return noveltiesLineBuilder.postTitleLine(item, ctx)
    }
    const { t, formatPrice } = ctx
    const prefix = item.name === 'bundle' ? t('collectionPrefix') : t('slipCasePrefix')
    return hasNonZeroPrice(item.price)
      ? t('collectionEntryLineCompact', {
          prefix,
          price: formatPrice(item.price),
          currency: '€',
        })
      : t('collectionEntryLineCompactNoPrice', { prefix })
  },
}

export const mapNoveltyItem = (d: NoveltyResultItem): PreviewEntryData => ({
  localizedSeriesTitle: d.localizedSeriesTitle,
  id: d.id,
  entryId: d.entryId,
  price: d.price,
  version: getVersionFromVolume(d),
  number: Number(d.number),
  name:
    d.relationType === NoveltyRelationTypeType.Collection && d.volumeSuffix
      ? d.volumeSuffix
      : undefined,
  releaseDateAccuracy: d.releaseDateAccuracy as CompatibleAccuracyType,
  releaseDate: d.releaseDate,
  publisherName: d.publisherName,
  cover: d.cover,
  thumbHash: d.thumbHash,
  relationType: d.relationType,
  relationId: d.relationId,
  collectionVolumes: d.releaseItems || [],
  collectionSpansEntries: d.collectionVolumeInfo?.spansEntries ?? false,
  collectionVolumeCount: d.collectionVolumeInfo?.volumeCount,
})

export const getPaginationItemsForNovelties = (
  data: ResultOf<typeof GET_FILTERED_NOVELTIES>,
): PreviewEntryData[] => {
  const items = ('filteredNovelties' in data && data.filteredNovelties) || []
  return items.map(mapNoveltyItem)
}

export const getSortForNovelties = (
  sorts: ReleaseEntrySorts,
): { orderBy: NoveltyOrderByInput[] } => {
  return {
    orderBy: [
      ...sorts
        .filter((s) => s.order != null)
        .map((s) => ({
          [s.property]: s.order,
        })),
      {
        number: OrderDirection.Asc,
      },
      {
        id: OrderDirection.Asc,
      },
    ],
  }
}

// A curated-list selection matches a novelty when it is the listed novelty
// (novelty lists) or shares the releaseEntry of a listed release (releaseEntry
// lists). Member ids come from the user-lists store, so both the Keystone and
// the SQL where paths only see scalar id/entryId conditions.
const membershipOrConditions = (listIds: string[]): NoveltyWhereInput[] => {
  const { noveltyIds, releaseEntryIds } = useUserListsStore().memberEntityKeysForLists(listIds)
  return [
    ...(noveltyIds.length > 0 ? [{ id: { in: noveltyIds } }] : []),
    ...(releaseEntryIds.length > 0 ? [{ entryId: { in: releaseEntryIds } }] : []),
  ]
}

const getCuratedListConditions = (filter: NoveltiesFilterProps): NoveltyWhereInput[] => {
  const conditions: NoveltyWhereInput[] = []
  if (filter.selectedCuratedLists && filter.selectedCuratedLists.length > 0) {
    const orConditions = membershipOrConditions(filter.selectedCuratedLists)
    // Empty selected lists must match nothing, not everything.
    conditions.push(orConditions.length > 0 ? { OR: orConditions } : { id: { in: [] } })
  }
  if (filter.excludedCuratedLists && filter.excludedCuratedLists.length > 0) {
    const orConditions = membershipOrConditions(filter.excludedCuratedLists)
    if (orConditions.length > 0) {
      conditions.push({ NOT: [{ OR: orConditions }] })
    }
  }
  return conditions
}

const getWhereForNoveltiesIncludingDisabled = (
  filter: NoveltiesFilterProps,
): { where: NoveltyWhereInput; filterMode: string | null } => {
  const tagsFilter = filter.selectedTags
    ? filter.selectedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const demographyFilter: NoveltyWhereInput[] = filter.selectedDemographies
    ? filter.selectedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const excludedTagsFilter: NoveltyWhereInput[] = filter.excludedTags
    ? filter.excludedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const excludedDemographyFilter: NoveltyWhereInput[] = filter.excludedDemographies
    ? filter.excludedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const normalizedSearchInput = filter.seriesTitle
    ? normalizeSearchInput(filter.seriesTitle).map((part) => ({ titleSearch: { contains: part } }))
    : []
  const ORConditions = [
    ...(filter.showFirstVolumes ? [{ number: { equals: '1.0' } }] : []),
    ...tagsFilter,
  ]
  const rangeActive = !!filter.startDate && !!filter.endDate
  const rangeAndConditions: NoveltyWhereInput[] = []
  if (rangeActive) {
    const start = dayjs(filter.startDate)
    const end = dayjs(filter.endDate)
    const sy = start.year()
    const sm = start.month() + 1
    const ey = end.year()
    const em = end.month() + 1
    if (sy === ey) {
      rangeAndConditions.push({ releaseYear: { equals: sy } })
      rangeAndConditions.push({ releaseMonth: { gte: sm, lte: em } })
    } else {
      const rangeOr: NoveltyWhereInput[] = [
        { AND: [{ releaseYear: { equals: sy } }, { releaseMonth: { gte: sm } }] },
        ...(ey - sy > 1
          ? [{ AND: [{ releaseYear: { gt: sy } }, { releaseYear: { lt: ey } }] }]
          : []),
        { AND: [{ releaseYear: { equals: ey } }, { releaseMonth: { lte: em } }] },
      ]
      rangeAndConditions.push({ OR: rangeOr })
    }
  }
  const hasSelectedMedium = !!filter.selectedMedium && filter.selectedMedium.length > 0
  const hasExcludedMedium = !!filter.excludedMedium && filter.excludedMedium.length > 0
  const typeFilter =
    hasSelectedMedium || hasExcludedMedium
      ? {
          ...(hasSelectedMedium ? { in: filter.selectedMedium } : {}),
          ...(hasExcludedMedium ? { notIn: filter.excludedMedium } : {}),
        }
      : null
  const hasSelectedPublisher = !!filter.selectedPublisher && filter.selectedPublisher.length > 0
  const hasExcludedPublisher = !!filter.excludedPublisher && filter.excludedPublisher.length > 0
  const publisherFilter =
    hasSelectedPublisher || hasExcludedPublisher
      ? {
          ...(hasSelectedPublisher ? { in: filter.selectedPublisher } : {}),
          ...(hasExcludedPublisher ? { notIn: filter.excludedPublisher } : {}),
        }
      : null
  const filterMode = filter.excludeFromCollection
    ? NOT_IN_COLLECTION_MODE
    : filter.onlyShowFromCollection
      ? (filter.collectionFilterMode ?? 'allInCollection')
      : null
  const curatedAndConditions = getCuratedListConditions(filter)
  return {
    where: {
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(publisherFilter ? { publisherName: publisherFilter } : {}),
      ...(!rangeActive && filter.selectedYear
        ? { releaseYear: { equals: filter.selectedYear } }
        : {}),
      ...(!rangeActive && filter.selectedMonth
        ? { releaseMonth: { equals: filter.selectedMonth } }
        : {}),
      ...(filter.selectedRelationType && filter.selectedRelationType.length > 0
        ? { relationType: { in: filter.selectedRelationType } }
        : {}),
      ...(filter.selectedPriceSpan
        ? {
            price: {
              gte: filter.selectedPriceSpan[0].toPrecision(2),
            },
          }
        : {}),
      ...(filter.selectedPriceSpan ||
      normalizedSearchInput.length > 0 ||
      rangeAndConditions.length > 0 ||
      demographyFilter.length > 0 ||
      excludedTagsFilter.length > 0 ||
      excludedDemographyFilter.length > 0 ||
      curatedAndConditions.length > 0
        ? {
            AND: [
              ...(filter.selectedPriceSpan
                ? [{ price: { lte: filter.selectedPriceSpan[1].toPrecision(2) } }]
                : []),
              ...normalizedSearchInput,
              ...rangeAndConditions,
              ...(demographyFilter.length > 0 ? [{ OR: demographyFilter }] : []),
              ...(excludedTagsFilter.length > 0 ? [{ NOT: [{ OR: excludedTagsFilter }] }] : []),
              ...(excludedDemographyFilter.length > 0
                ? [{ NOT: [{ OR: excludedDemographyFilter }] }]
                : []),
              ...curatedAndConditions,
            ],
          }
        : {}),
      ...(ORConditions.length > 0
        ? {
            OR: ORConditions,
          }
        : {}),
    },
    filterMode: withCollectionRefinementSuffixes(filter, filterMode),
  }
}

const withCollectionRefinementSuffixes = (
  filter: NoveltiesFilterProps,
  filterMode: string | null,
): string | null => {
  if (filterMode === null || filterMode === NOT_IN_COLLECTION_MODE) return filterMode
  return `${filterMode}${filter.showNoDuplicates ? '|doNotShowAlternativesWhenOwned' : ''}${filter.hidePlanned ? '|hidePlanned' : ''}${filter.hideCollecting ? '|hideCollecting' : ''}${filter.hidePaused ? '|hidePaused' : ''}`
}

export const getWhereForNovelties = (
  filter: NoveltiesFilterProps,
): { where: NoveltyWhereInput; filterMode: string | null } => {
  const { where, filterMode } = getWhereForNoveltiesIncludingDisabled(filter)
  return { where: { ...where, disabled: { equals: false } }, filterMode }
}

export const getWhereForCuratedListNovelties = getWhereForNoveltiesIncludingDisabled
